import { Duration } from 'luxon';

/**
 * Branded template literal type for anchor-based durations.
 * Represents durations like P1D@12:00 (1 day ending at 12:00).
 *
 * Note: Uses `${number}` for days to avoid excessive union members,
 * but hours and minutes are strictly typed.
 */
declare const __anchorDurationBrand: unique symbol;
// TODO: the left part could be any ISO duration not just days, so need to refactor this type later
export type AnchorDuration = `P${number}D@${number}:${number}` & {
  readonly [__anchorDurationBrand]: 'AnchorDuration';
};

/**
 * Parsed components of an anchor duration.
 */
export interface AnchorDurationParts {
  days: number;
  hour: number;
  minute: number;
}

/**
 * Type guard to check if a value is an AnchorDuration.
 * Validates the full format: P{days}D@{HH}:{mm}
 */
export const isAnchorDuration = (
  duration: string | Duration | undefined | null | number
): duration is AnchorDuration => {
  return (
    typeof duration === 'string' &&
    /^P\d+D@([01]\d|2[0-3]):[0-5]\d$/.test(duration)
  );
};

const DEFAULT_ANCHOR_PARTS: AnchorDurationParts = {
  days: 1,
  hour: 11,
  minute: 0,
};

/**
 * Parse anchor duration into components.
 * @param duration - Format: P3D@11:00
 * @returns { days: 3, hour: 11, minute: 0 }
 */
export const parseAnchorDuration = (
  duration?: string | AnchorDuration
): AnchorDurationParts => {
  if (!duration || !isAnchorDuration(duration)) {
    return DEFAULT_ANCHOR_PARTS;
  }

  const [durationPart, timePart] = duration.split('@');

  const durationObj = Duration.fromISO(durationPart);
  if (!durationObj.isValid) {
    return DEFAULT_ANCHOR_PARTS;
  }

  const timeMatch = timePart.match(/^(\d{2}):(\d{2})$/);
  if (!timeMatch) {
    return DEFAULT_ANCHOR_PARTS;
  }

  const [, hour, minute] = timeMatch;
  return {
    days: Math.floor(durationObj.as('days')),
    hour: parseInt(hour, 10),
    minute: parseInt(minute, 10),
  };
};

/**
 * Format anchor duration for human-readable display.
 * @param duration - P3D@11:00 or P3D@23:00
 * @returns "3 nights (until 11:00 AM)" or "3 nights (until 11:00 PM)"
 */
export const formatAnchorDuration = (duration: AnchorDuration): string => {
  const { days, hour, minute } = parseAnchorDuration(duration);

  // Convert 24h to 12h format with AM/PM.
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  const timeStr = `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;

  const nightsText = `${days} night${days > 1 ? 's' : ''}`;
  return `${nightsText} (until ${timeStr})`;
};

/**
 * Build anchor duration string from components.
 * @returns Branded AnchorDuration type
 */
export const buildAnchorDuration = (
  days: number,
  hour: number,
  minute: number
): AnchorDuration => {
  const hourStr = hour.toString().padStart(2, '0');
  const minuteStr = minute.toString().padStart(2, '0');
  return `P${days}D@${hourStr}:${minuteStr}` as AnchorDuration;
};

/**
 * Format any duration type for display in DataGrid or UI.
 */
export const formatDuration = (duration: Duration | AnchorDuration): string => {
  if (isAnchorDuration(duration)) {
    return formatAnchorDuration(duration);
  }

  if (duration instanceof Duration) {
    return duration.toHuman();
  }

  // Handle presets.
  if (duration === 'all_day') return 'All day';
  if (duration === 'until_5am_next_day') return 'Until 5 AM next day';
  if (duration === 'usage') return 'Usage-based';

  return String(duration);
};
